"use client";

import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { useTranslations } from "next-intl";

import {
  ExtendedMunicipalityRecord,
  GeoStatus,
  IconType,
  FilterMunicipalityValue,
  FilterSettings,
  ICoordinates,
} from "@types";

import { Button } from "components/atoms/Button/Button";
import { CancelButton } from "components/atoms/CancelButton/CancelButton";
import { useGetGeoLocation } from "utils";
import { getDistanceFromLatLonInKm } from "utils/distance";
import { SEARCH_DELAY } from "consts";

import { Label } from "../fields/Label";
import { customStyles } from "../styles/reactSelectStyles";
import s from "./LocationFilter.module.scss";

interface MapboxFeature {
  text: string;
  place_type: string[];
  center: [number, number];
  context?: { id: string; text: string }[];
}

const MAPBOX_API_URL = "https://api.mapbox.com/geocoding/v5/mapbox.places";
const MAPBOX_PARAMS = "types=place,locality,neighborhood,postcode&country=BE,LU,DE&language=nl&limit=5";

// Pure helper functions
const createMunicipalityValue = (
  city: string,
  postcode: string,
  coordinates: ICoordinates,
): FilterMunicipalityValue => ({
  value: { city, postalCode: postcode, coordinates } as ExtendedMunicipalityRecord,
  label: postcode ? `${city} (${postcode})` : city,
});

const extractCityAndPostcode = (feature: MapboxFeature): [string, string] => {
  const isPostcodeType = feature.place_type?.includes("postcode");
  
  if (isPostcodeType) {
    const city = feature.context?.find((c) => c.id.startsWith("place"))?.text || feature.text;
    return [city, feature.text];
  }
  
  return [feature.text, ""];
};

const mapFeatureToOption = (feature: MapboxFeature): FilterMunicipalityValue => {
  const [longitude, latitude] = feature.center;
  const [city, postcode] = extractCityAndPostcode(feature);
  return createMunicipalityValue(city, postcode, { latitude, longitude });
};

const compareDistance = (userCoords: ICoordinates) => (
  a: FilterMunicipalityValue,
  b: FilterMunicipalityValue,
): number => {
  const distA = getDistanceFromLatLonInKm(
    a.value.coordinates.latitude,
    a.value.coordinates.longitude,
    userCoords.latitude,
    userCoords.longitude,
  );
  const distB = getDistanceFromLatLonInKm(
    b.value.coordinates.latitude,
    b.value.coordinates.longitude,
    userCoords.latitude,
    userCoords.longitude,
  );
  return distA - distB;
};

const sortByDistance = (
  options: FilterMunicipalityValue[],
  userCoords?: ICoordinates | null,
): FilterMunicipalityValue[] =>
  userCoords ? [...options].sort(compareDistance(userCoords)) : options;

const buildMapboxUrl = (searchTerm: string, token: string): string =>
  `${MAPBOX_API_URL}/${encodeURIComponent(searchTerm)}.json?access_token=${token}&${MAPBOX_PARAMS}`;

const fetchMapboxLocations = async (searchTerm: string): Promise<FilterMunicipalityValue[]> => {
  const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
  if (!token) return [];

  try {
    const response = await fetch(buildMapboxUrl(searchTerm, token));
    if (!response.ok) return [];
    
    const json = await response.json();
    return json.features?.map(mapFeatureToOption) || [];
  } catch {
    return [];
  }
};

const getSelectOptions = (value: FilterMunicipalityValue | null, options: FilterMunicipalityValue[]) =>
  value && options.length === 0 ? [value] : options;

// Custom hook for location search
const useLocationSearch = (currentCoordinates?: ICoordinates | null) => {
  const [options, setOptions] = useState<FilterMunicipalityValue[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTermRef = useRef("");

  const search = useCallback(
    async (searchTerm: string) => {
      searchTermRef.current = searchTerm;
      setIsSearching(true);

      const results = await fetchMapboxLocations(searchTerm);

      if (searchTermRef.current === searchTerm) {
        setOptions(sortByDistance(results, currentCoordinates));
        setIsSearching(false);
      }
    },
    [currentCoordinates],
  );

  const clearSearch = useCallback(() => {
    searchTermRef.current = "";
    setOptions([]);
    setIsSearching(false);
  }, []);

  return { options, isSearching, search, clearSearch };
};

// Custom hook for debounced input
const useDebouncedSearch = (search: (term: string) => void, clearSearch: () => void) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback(
    (inputValue: string, actionMeta: { action: string }) => {
      if (actionMeta.action !== "input-change") return;

      if (timerRef.current) clearTimeout(timerRef.current);

      if (inputValue.length <= 1) {
        clearSearch();
        return;
      }

      timerRef.current = setTimeout(() => search(inputValue), SEARCH_DELAY);
    },
    [search, clearSearch],
  );
};

// Custom hook for geolocation status
const useGeoStatusLabel = (status: GeoStatus, municipality: string, isActive: boolean, t: any) =>
  useMemo(() => {
    const statusMap: Record<GeoStatus, string> = {
      [GeoStatus.Pending]: `${t("pending-location")}...`,
      [GeoStatus.Success]: `Locatie: ${municipality}`,
      [GeoStatus.Timeout]: t("error-location"),
      [GeoStatus.Unavailable]: t("error-location"),
      [GeoStatus.Denied]: t("error-location"),
      [GeoStatus.Idle]: t("use-location"),
      [GeoStatus.Geocoding]: t("use-location"),
    };
    return isActive ? statusMap[status] : t("use-location");
  }, [status, municipality, isActive, t]);

interface Props {
  label: string;
  id: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  defaultValueLabel?: string;
  settings?: FilterSettings;
}

export const LocationFilter: FC<Props> = ({
  label,
  id,
  control,
  setValue,
  defaultValueLabel,
  settings,
}) => {
  const t = useTranslations("search");
  const { currentCoordinates, currentMunicipality, getGeoCodedLocation, status } = useGetGeoLocation();
  const [geoActive, setGeoActive] = useState(false);

  const { options, isSearching, search, clearSearch } = useLocationSearch(currentCoordinates);
  const handleInputChange = useDebouncedSearch(search, clearSearch);
  const statusLabel = useGeoStatusLabel(status, currentMunicipality, geoActive, t);

  useEffect(() => {
    if (currentCoordinates && currentMunicipality) {
      setValue(id, createMunicipalityValue(currentMunicipality, "", currentCoordinates));
      setGeoActive(true);
    }
  }, [currentCoordinates, currentMunicipality, setValue, id]);

  const handleChange = useCallback(
    (selected: FilterMunicipalityValue | null, onChange: (value: any) => void) => {
      setGeoActive(false);
      onChange(selected);
      if (selected) clearSearch();
    },
    [clearSearch],
  );

  const handleClear = useCallback(() => {
    setValue(id, "");
    setGeoActive(false);
  }, [setValue, id]);

  return (
    <div>
      <Label id={id} label={label} />
      <Controller
        name={id}
        control={control}
        render={({ field: { onChange, value, ref } }) => (
          <Select
            ref={ref}
            inputId={id}
            instanceId={id}
            options={getSelectOptions(value, options)}
            noOptionsMessage={() => t("input-municipality")}
            value={value}
            isClearable
            placeholder={defaultValueLabel || `${t("input-municipality")}...`}
            isLoading={isSearching}
            onInputChange={handleInputChange}
            styles={customStyles}
            onChange={(selected) => handleChange(selected, onChange)}
          />
        )}
      />
      {settings?.showCurrentLocation && (
        <div className={s.Actions}>
          <Button
            label={statusLabel}
            theme="textual"
            onClick={!geoActive ? getGeoCodedLocation : undefined}
            icon={IconType.Location}
            iconSize="l"
            preIcon
            disabled={status === GeoStatus.Pending}
          />
          {geoActive && <CancelButton onClick={handleClear} />}
        </div>
      )}
    </div>
  );
};
